import { Badge } from '@/components/base/badges/badges';
import type { Container } from '@/types/container';
import { useI18n } from '@/hooks/use-i18n';
import { Flag01 } from '@untitledui/icons';

interface ContainerRouteCardProps {
    container: Container;
    onSelect?: (containerId: string) => void;
    showFlag?: boolean;
}

export const ContainerRouteCard = ({ container, onSelect, showFlag = false }: ContainerRouteCardProps) => {
    const { translateContainerStatus } = useI18n();

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'In Transit': return 'brand';
            case 'Delivered': return 'success';
            case 'Delayed': return 'error';
            case 'At Port': return 'warning';
            default: return 'gray';
        }
    };

    // Formatta ETA
    const formatETA = (eta: Date) => {
        const days = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
        const months = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
        const day = days[eta.getDay()];
        const date = eta.getDate();
        const month = months[eta.getMonth()];
        const year = eta.getFullYear();
        return `${day}, ${date} ${month} ${year}`;
    };

    return (
        <div
            className="rounded-lg bg-primary p-4 shadow-xs ring-1 ring-secondary hover:ring-2 hover:shadow-sm transition-all cursor-pointer"
            onClick={() => onSelect?.(container.id)}
        >
            <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-md font-semibold text-primary">{container.containerNumber}</h4>
                        {showFlag && (
                            <Flag01 className="size-4 text-tertiary" />
                        )}
                    </div>
                    
                    {/* Route con indicatori */}
                    <div className="flex items-center gap-2 mb-2">
                        <div className="flex items-center gap-1.5">
                            <div className="size-2 rounded-full bg-brand-600"></div>
                            <span className="text-sm text-primary font-medium">{container.route.from}</span>
                        </div>
                        <span className="text-sm text-tertiary">→</span>
                        <div className="flex items-center gap-1.5">
                            <div className="size-2 rounded-full bg-gray-400"></div>
                            <span className="text-sm text-primary font-medium">{container.route.to}</span>
                        </div>
                    </div>

                    {/* ETA */}
                    <div className="text-sm text-tertiary mb-2">
                        ETA {formatETA(container.eta)}
                    </div>

                    {/* Customer/Shipper info - mostra shipping company se disponibile */}
                    {container.shippingCompany && (
                        <div className="text-xs text-tertiary mb-2">
                            {container.shippingCompany}
                        </div>
                    )}

                    {/* PO Numbers - se disponibili nel container */}
                    {container.quoteRequestId && (
                        <div className="text-xs text-tertiary">
                            PO: {container.quoteRequestId.substring(0, 12)}...
                        </div>
                    )}
                </div>

                {/* Status badge */}
                <Badge color={getStatusColor(container.status)} size="sm">
                    {translateContainerStatus(container.status)}
                </Badge>
            </div>
        </div>
    );
};

